/**
 * ManualThemeDialog — directive (no-AI) theme creation.
 *
 * Posts to /api/agent/content-plan/themes/manual which:
 *   - Validates audience binding (audienceFit[] OR appliesToAllAudiences with rationale)
 *   - Validates dataUsage.items[] non-empty
 *   - Writes through ThemeCatalogService (centralised arrayUnion path)
 *
 * Mounted by ThemeCatalogDialog "+ Add manually" button.
 */

import React, { useState } from 'react';
import {
  Stack,
  TextField,
  Alert,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import { ContentPlanApiClient } from '@modules/content-plan/services/ContentPlanApiClient';
import {
  AudienceFitFormSection,
  AUDIENCE_FIT_FORM_EMPTY_VALUE,
  type AudienceFitFormSectionValue,
} from '@modules/content-plan/components/strategy/AudienceFitFormSection';

export interface ManualThemeDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  onCreated?: (themeId: string) => void;
}

export const ManualThemeDialog: React.FC<ManualThemeDialogProps> = ({
  open,
  onClose,
  projectId,
  userId,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audienceForm, setAudienceForm] = useState<AudienceFitFormSectionValue>(AUDIENCE_FIT_FORM_EMPTY_VALUE);
  const [dataUsageRaw, setDataUsageRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setAudienceForm(AUDIENCE_FIT_FORM_EMPTY_VALUE);
      setDataUsageRaw('');
      setError(null);
    }
  }, [open]);

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required.';
    const hasAudienceFit = audienceForm.audienceFit.length > 0;
    const universal = audienceForm.appliesToAllAudiences;
    if (!hasAudienceFit && !universal) {
      return 'Set audienceFit[] or mark "Applies to all audiences" with a rationale.';
    }
    if (hasAudienceFit && universal) {
      return '"Applies to all audiences" is mutually exclusive with per-audience fits.';
    }
    if (universal && !audienceForm.appliesToAllAudiencesRationale.trim()) {
      return 'Universal themes require a rationale.';
    }
    if (hasAudienceFit) {
      const missingRationale = audienceForm.audienceFit.find(
        (f) => !(f.rationale ?? '').trim(),
      );
      if (missingRationale) return 'Every audience fit needs a rationale.';
    }
    const usageLines = dataUsageRaw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (usageLines.length === 0) {
      return 'Provide at least one dataUsage source path (one per line, e.g. @insight[an_x].audienceContentBriefs[aud_y]).';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const items = dataUsageRaw
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((sourcePath) => ({ sourcePath }));
      const client = new ContentPlanApiClient();
      const audienceFit = audienceForm.audienceFit.map((f) => ({
        audienceBriefRef: f.audienceBriefRef!,
        fit: f.fit as 'strong' | 'medium' | 'weak',
        rationale: f.rationale,
        // dataUsage on each fit is optional in the API contract.
      }));
      const result = await client.createManualTheme({
        projectId,
        userId,
        name: name.trim(),
        description: description.trim() || undefined,
        audienceFit: audienceFit.length > 0 ? audienceFit : undefined,
        appliesToAllAudiences: audienceForm.appliesToAllAudiences || undefined,
        appliesToAllAudiencesRationale: audienceForm.appliesToAllAudiences
          ? audienceForm.appliesToAllAudiencesRationale
          : undefined,
        dataUsage: { items },
      });
      if (!result.success) throw new Error(result.error ?? 'Theme creation failed');
      onCreated?.(result.themeId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      data-testid="manual-theme-dialog"
    >
      <DialogHeader title="Create theme manually" onClose={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Theme name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            inputProps={{ 'data-testid': 'manual-theme-name' }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ 'data-testid': 'manual-theme-description' }}
          />

          <Divider>Audience binding</Divider>

          <AudienceFitFormSection
            projectId={projectId}
            value={audienceForm}
            onChange={setAudienceForm}
          />

          <Divider>Data citations</Divider>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              At least one source path. One per line, e.g.
              <code style={{ marginLeft: 4 }}>@insight[an_x].audienceContentBriefs[aud_y]</code>
            </Typography>
            <TextField
              value={dataUsageRaw}
              onChange={(e) => setDataUsageRaw(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="@insight[an_health].audienceContentBriefs[hc_vp_ops]"
              inputProps={{ 'data-testid': 'manual-theme-data-usage' }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          'data-testid': 'manual-theme-cancel',
        } as any}
        primaryAction={{
          label: submitting ? 'Saving…' : 'Create theme',
          onClick: handleSave,
          disabled: submitting,
          'data-testid': 'manual-theme-submit',
        } as any}
      />
    </BaseDialog>
  );
};

export default ManualThemeDialog;
